import React, { useContext, useState, useCallback, useEffect } from 'react';

import { Context, useFetch } from 'store';
import { Card, Button, Icon, Tabs, Modal, Divider } from 'antd';

import { PageContainer } from '_src/shared/styles';
import { Input, Select } from '_src/shared/form-helpers';
import { debounce } from '_src/utils';
import services from '_src/services';
import { Refresh, AsyncTable, Table } from '_src/components';
import * as XLSX from 'xlsx';

import { ButtonContainer, ModalContainer, ModalHeaderContainer } from './styles';

const { TabPane } = Tabs;

export default () => {
  const { setShown, getShown, refreshResource } = useContext(Context);
  const resource = 'privileges';
  const config = services['privileges'];

  const { get: getLocations, busy } = useFetch({ ...config.getLocations });
  const { get: getGroups } = useFetch({ ...config.getGroups });

  const [activeKey, setActiveKey] = useState('tab1');
  const [locations, setLocations] = useState([]);
  const [allResources, setAllResources] = useState([]);

  const [branchId, setBranchId] = useState('all');
  const [resourceFilter, setResource] = useState('all');
  const [studentName, setStudentName] = useState('');
  const [groupName, setGroupName] = useState('all');
  const [email, setEmail] = useState('');
  const [filterParams, setFilterParams] = useState({});
  const [previousFilters, setPreviousFilters] = useState({});

  // users list
  const [usersList, setUsersList] = useState([]);
  const [userFullName, setUserFullName] = useState('');
  const [currentPrivilege, setCurrentPrivilege] = useState('');

  // groups
  const [allGroups, setAllGroups] = useState([]);

  useEffect(() => {
    setAllResources(
      Object.values(
        GLOBALS.PRIVILEGES.reduce((acc, p) => {
          acc[p.resource] = !acc[p.resource] || p.title ? p : acc[p.resource];
          return acc;
        }, {})
      )
    );

    getLocations().then(branches => {
      setLocations([{ id: `all`, label: `All Branches` }, ...branches]);

      getGroups().then(data => {
        let groups = data.groups.map(({ users, ...rest }) => ({
          ...rest,
          users: (users || []).map(user => ({
            ...user,
            fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            branchName: branches.find(branch => user.branchId === branch.id)?.label || null
          }))
        }));

        setAllGroups(groups);
      });
    });
  }, []);

  const debounceCallback = useCallback(
    debounce(
      params => {
        setFilterParams(params);
      },
      1000,
      false
    ),
    []
  );

  const filteredUsers = usersList.filter(user => {
    const search = userFullName.toLowerCase();

    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.username?.toLowerCase().includes(search)
    );
  });

  const filteredGroups = (allGroups || [])
    .map(group => {
      const filteredUsers = (group.users || []).filter(user => {
        const search = userFullName.toLowerCase().trim();

        if (!search) return true;

        return (
          user.fullName?.toLowerCase().includes(search) ||
          user.email?.toLowerCase().includes(search) ||
          user.username?.toLowerCase().includes(search)
        );
      });

      return {
        ...group,
        users: filteredUsers
      };
    })
    .filter(group => group.users.length > 0);

  const handleDownload = (record = [], currentPrivilege) => {
    const data = record.map(u => ({
      Username: u.username,
      Email: u.email,
      Name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
      Branch: locations.find(b => b.id === u.branchId)?.label || ''
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${currentPrivilege}.xlsx`);
  };

  const columns = [
    { title: 'Username', dataIndex: 'username', key: 'username' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Name', dataIndex: 'fullName', key: 'fullName' },
    { title: 'Branch', dataIndex: 'branchName', key: 'branchName' }
  ];

  const groupColumns = [{ title: 'Group Name', dataIndex: 'groupName', key: 'groupName' }];

  return (
    <PageContainer>
      <Refresh />
      <Card>
        <Tabs activeKey={activeKey} onChange={setActiveKey}>
          <TabPane tab="Privileges" key="tab1">
            <ButtonContainer>
              <Select
                label="Branches"
                showSearch
                onChange={locationSelected => {
                  setBranchId(locationSelected);

                  const { studentName, instructor, ...restParams } = filterParams;
                  if (locationSelected === 'all') {
                    setFilterParams(prev => {
                      const { branchId, ...rest } = prev;
                      return rest;
                    });
                  } else {
                    setFilterParams(() => {
                      return {
                        ...restParams,
                        branchId: locationSelected
                      };
                    });
                  }
                }}
                options={locations}
                value={branchId}
              />

              <Select
                showSearch
                options={[
                  { id: 'all', label: 'All' },
                  ...allResources.map(r => ({
                    id: r.resource,
                    label: r.title
                  }))
                ]}
                label="Filter by Privilege"
                value={resourceFilter}
                onChange={r => {
                  setResource(r);
                  if (r === 'all') {
                    const { resource, ...restParams } = filterParams;
                    setFilterParams(restParams);
                    return;
                  }

                  debounceCallback({
                    ...filterParams,
                    resource: r
                  });
                }}
              />

              <Input
                label="Full Name"
                onChange={e => {
                  if (e.length === 0) {
                    setStudentName('');
                    const { firstName, lastName, ...restParams } = filterParams;
                    debounceCallback(restParams);
                    return;
                  }

                  const [firstName, lastName = ''] = e.split(' ');
                  setStudentName(e);
                  debounceCallback({
                    ...filterParams,
                    firstName,
                    ...(lastName ? { lastName } : {})
                  });
                }}
                value={studentName}
                allowClear
              />

              <Input
                label="Email"
                onChange={e => {
                  if (e.length === 0) {
                    setEmail('');
                    const { email, ...restParams } = filterParams;
                    debounceCallback(restParams);
                    return;
                  }

                  setEmail(e);
                  debounceCallback({
                    ...filterParams,
                    email
                  });
                }}
                value={email}
                allowClear
              />
              <Select
                options={[
                  { id: 'all', label: 'All' },
                  ...allGroups.map(g => ({
                    id: g.groupId,
                    label: g.groupName
                  }))
                ]}
                label="Filter by Group"
                value={groupName}
                onChange={group => {
                  setGroupName(group);
                  if (group === 'all') {
                    const { groupName, ...restParams } = filterParams;
                    setFilterParams(restParams);
                    return;
                  }

                  debounceCallback({
                    ...filterParams,
                    groupName: allGroups.find(g => g.groupId === group).groupName
                  });
                }}
              />
            </ButtonContainer>

            <AsyncTable
              resource={resource}
              fileName={resource}
              pagination
              params={filterParams}
              // onChange={onFiltersChange}
              config={{
                ...config,
                table: [
                  ...config.table,
                  {
                    title: 'Users list',
                    // width: '200px',
                    render: (_, { resource, users }) => {
                      console.log('test');
                      return (
                        <a onClick={event => event.stopPropagation()}>
                          <Button
                            type="primary"
                            onClick={() => {
                              setCurrentPrivilege(
                                allResources.find(p => p.resource === resource)?.title
                              );
                              setUsersList(
                                (users || []).map(u => ({
                                  ...u,
                                  name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
                                  branchName:
                                    locations.find(branch => u.branchId === branch.id)?.label ||
                                    null
                                }))
                              );
                              setShown('usersList');
                            }}
                          >
                            View
                          </Button>
                          <Button
                            type="primary"
                            onClick={() => {
                              setCurrentPrivilege(
                                allResources.find(p => p.resource === resource)?.title
                              );
                              handleDownload(
                                users,
                                allResources.find(p => p.resource === resource)?.title
                              );
                            }}
                            style={{ marginLeft: '20px' }}
                          >
                            Download
                          </Button>
                          {/* <Icon type="eye" style={{ marginLeft: '20px' }} />
                          <Icon type="download" style={{ marginLeft: '40px' }} /> */}
                        </a>
                      );
                    }
                  }
                ]
              }}
              onChange={(_, __, sorter) => {
                if (!sorter.order) {
                  const { sortBy, sortOrder, ...restParams } = filterParams;
                  setFilterParams(restParams);
                  return;
                }
                if (sorter.field === 'resource') {
                  setFilterParams({
                    ...filterParams,
                    ...(sorter.field && {
                      sortBy: 'resource',
                      sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc'
                    })
                  });
                }
              }}
            />

            <Modal
              visible={getShown('usersList')}
              onCancel={() => setShown('usersList', false)}
              width="70%"
              style={{ maxWidth: 1200 }}
              footer={[
                <Button key="cancel" onClick={() => setShown('usersList', false)}>
                  Close
                </Button>
              ]}
              destroyOnClose={true}
            >
              <ModalContainer>
                <ModalHeaderContainer>
                  Users with privilege: <b>{currentPrivilege}</b>
                </ModalHeaderContainer>

                <Divider />
                <div
                  style={{
                    width: '100%',
                    margin: 'auto',
                    overflow: 'hidden'
                  }}
                >
                  <Input
                    label="Filter by name or email"
                    onChange={value => {
                      setUserFullName(value);
                    }}
                    value={userFullName}
                    style={{ width: '250px', marginBottom: '40px' }}
                    prefix={<Icon type="search" style={{ color: '#909090' }} />}
                  />
                  <Table
                    pagination
                    data={filteredUsers}
                    columns={[
                      {
                        title: 'Username',
                        dataIndex: 'username',
                        key: 'username'
                      },
                      {
                        title: 'Email',
                        dataIndex: 'email',
                        key: 'email'
                      },
                      {
                        title: 'Name',
                        dataIndex: 'name',
                        key: 'name'
                      },
                      {
                        title: 'Branch',
                        dataIndex: 'branchName',
                        key: 'branchName'
                      }
                    ]}
                    fileName={resource}
                  />
                </div>
              </ModalContainer>
            </Modal>
          </TabPane>

          <TabPane tab="Groups" key="tab2">
            <Input
              label="Filter by name or email"
              onChange={value => {
                setUserFullName(value);
              }}
              value={userFullName}
              style={{ width: '250px', marginBottom: '5px' }}
              prefix={<Icon type="search" style={{ color: '#909090' }} />}
              allowClear
            />
            <Table
              dataSource={filteredGroups}
              columns={groupColumns}
              rowKey="group"
              expandedRowRender={record => (
                <Table columns={columns} dataSource={record.users} pagination rowKey="username" />
              )}
            />
          </TabPane>
        </Tabs>
      </Card>
    </PageContainer>
  );
};
